import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { formatIST } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import api, {
  skipJobCardStage, moveJobCardToStage, insertJobCardStage, removeJobCardStage,
} from '@/lib/api';
import { toast } from 'sonner';
import {
  ArrowLeft, Cog, AlertTriangle, ChevronRight, RefreshCw,
  Calendar, Package, Layers, CheckCircle2, Clock, Printer,
  QrCode, ArrowRight, ArrowLeftIcon, MessageSquare,
  Truck, ChevronDown, ChevronUp, Edit, ExternalLink,
  SkipForward, Shuffle, Plus, Trash2, GitBranch, FileText
} from 'lucide-react';

import { PRIORITY_COLORS, STATUS_COLORS } from '@/lib/status-colors';
import { downloadJobCardQr } from '@/lib/jobCardQr';
import { openAuthedFile } from '@/lib/fileDownload';

import {
  Screen, ScreenSplit, Pane, CommandBar, DataGrid, FactBand, Fact, CellButton,
} from '@/components/data';
import { needsPlate } from '@/lib/plate';

const ASSIGNMENT_TYPES = [
  { key: 'binding_folding', label: 'Binding/Folding' },
  { key: 'lamination', label: 'Lamination' },
  { key: 'uv_printing', label: 'UV Printing' },
  { key: 'foil_printing', label: 'Foil Printing' },
  { key: 'other', label: 'Other' },
];

export default function SupervisorJobCardDetailPage() {
  const { id: jobCardId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [jobCard, setJobCard] = useState(null);
  const [loading, setLoading] = useState(true);

  // Move forward/backward state
  const [moveDialog, setMoveDialog] = useState(null); // { direction, batchId, batchQty, fromStage, toStage }
  const [moveQtyMode, setMoveQtyMode] = useState('full');
  const [moveQty, setMoveQty] = useState('');
  const [moveReason, setMoveReason] = useState('');
  const [moveLoading, setMoveLoading] = useState(false);

  // Machine assignment state
  const [machineDialog, setMachineDialog] = useState(null); // { batch, stageName }
  const [machines, setMachines] = useState([]);
  const [loadingMachines, setLoadingMachines] = useState(false);
  const [assigning, setAssigning] = useState(false);

  // Remark state
  const [remarkText, setRemarkText] = useState('');
  const [addingRemark, setAddingRemark] = useState(false);

  // Send to vendor state
  const [vendorDialog, setVendorDialog] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState('');
  const [vendorAssignType, setVendorAssignType] = useState('binding_folding');
  const [vendorInstructions, setVendorInstructions] = useState('');
  const [vendorExpectedDate, setVendorExpectedDate] = useState('');
  const [sendingVendor, setSendingVendor] = useState(false);

  // Section collapse state
  const [specsExpanded, setSpecsExpanded] = useState(true);
  const [timelineExpanded, setTimelineExpanded] = useState(true);

  // Processes this product type runs that the job does not have yet. A stage
  // outside the registry has no supervisor linked to it and no machine, so the
  // job would stand at a step nobody can see.
  const addableStages = useMemo(() => {
    const onJob = new Set((jobCard?.production_stages || []).map(s => s.stage_key));
    return (jobCard?.product_config?.stage_generation_rules || [])
      .filter(rule => rule.stage_key && !onJob.has(rule.stage_key))
      .filter(rule => !['packing', 'dispatch', 'stock_in'].includes(rule.stage_key));
  }, [jobCard]);

  const fetchJobCard = useCallback(async () => {
    if (!jobCardId) return;
    try {
      const res = await api.get(`/job-cards/${jobCardId}`);
      setJobCard(res.data);
    } catch (e) {
      toast.error('Failed to load job card');
    }
    setLoading(false);
  }, [jobCardId]);

  useEffect(() => { fetchJobCard(); }, [fetchJobCard]);

  const userRoles = user?.roles || [user?.role];
  const canMoveStages = userRoles.some(r => ['admin', 'supervisor'].includes(r));

  // ─── Supervisor override state ───
  const [skipDialog, setSkipDialog] = useState(null); // { batchId, stageName }
  const [skipReason, setSkipReason] = useState('');
  const [rerouteDialog, setRerouteDialog] = useState(null); // { batchId, stageName }
  const [rerouteTarget, setRerouteTarget] = useState('');
  const [rerouteReason, setRerouteReason] = useState('');
  const [insertDialog, setInsertDialog] = useState(false);
  const [insertStageKey, setInsertStageKey] = useState('');
  const [insertAfter, setInsertAfter] = useState('__start__');
  const [removeDialog, setRemoveDialog] = useState(false);
  const [removeKey, setRemoveKey] = useState('');
  const [overrideLoading, setOverrideLoading] = useState(false);

  // ─── Machine Assignment ───
  const openMachineAssign = async (batch) => {
    const stageName = batch?.current_stage_name || stages[batch?.current_stage_index || 0]?.stage_name || '';
    setMachineDialog({ batch, stageName });
    setLoadingMachines(true);
    try {
      const res = await api.get(`/production/operator-assignments/stages/${encodeURIComponent(stageName)}/operators`);
      const machineList = res.data.machines || [];
      setMachines(machineList);
      if (machineList.length === 1) {
        await assignMachine(batch, machineList[0]);
      }
    } catch (e) {
      setMachines([]);
    }
    setLoadingMachines(false);
  };

  const assignMachine = async (batch, machine) => {
    setAssigning(true);
    try {
      await api.post(`/production/operator-assignments/batches/${batch.batch_id || batch.id}/assign`, {
        stage_name: batch.current_stage_name || stages[batch.current_stage_index || 0]?.stage_name,
        machine_id: machine.id,
      });
      toast.success(`Assigned to ${machine.machine_name}`);
      setMachineDialog(null);
      fetchJobCard();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Assignment failed');
    }
    setAssigning(false);
  };

  // ─── Move Forward/Backward ───
  const openMoveDialog = (direction, batchId, batchQty, fromStage, toStage) => {
    setMoveDialog({ direction, batchId, batchQty, fromStage, toStage });
    setMoveQtyMode('full');
    setMoveQty(String(batchQty));
    setMoveReason('');
  };

  const handleMoveConfirm = async () => {
    if (!moveDialog) return;
    const qty = moveQtyMode === 'full' ? moveDialog.batchQty : parseInt(moveQty, 10);
    if (!qty || qty <= 0 || qty > moveDialog.batchQty) {
      toast.error(`Enter a quantity between 1 and ${moveDialog.batchQty}`);
      return;
    }
    setMoveLoading(true);
    try {
      const body = { quantity: qty };
      if (moveDialog.batchId) body.batch_id = moveDialog.batchId;
      // the server reports what it actually did — this toast used to describe a
      // split the backend was silently dropping
      let res;
      if (moveDialog.direction === 'forward') {
        res = await api.post(`/job-cards/${jobCardId}/move-forward`, body);
      } else {
        body.reason = moveReason || undefined;
        res = await api.post(`/job-cards/${jobCardId}/move-backward`, body);
      }
      toast.success(res.data?.message || 'Stage moved');
      setMoveDialog(null);
      fetchJobCard();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to move stage');
    }
    setMoveLoading(false);
  };

  // ─── Supervisor Overrides: skip / reroute / insert / remove ───
  const openSkipDialog = (batchId, stageName) => {
    setSkipDialog({ batchId, stageName });
    setSkipReason('');
  };

  const handleSkip = async () => {
    if (!skipDialog) return;
    setOverrideLoading(true);
    try {
      await skipJobCardStage(jobCardId, {
        batch_id: skipDialog.batchId || undefined,
        reason: skipReason || undefined,
      });
      toast.success(`Skipped ${skipDialog.stageName || 'current stage'}`);
      setSkipDialog(null);
      fetchJobCard();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to skip stage');
    }
    setOverrideLoading(false);
  };

  const openRerouteDialog = (batchId, stageName) => {
    setRerouteDialog({ batchId, stageName });
    setRerouteTarget('');
    setRerouteReason('');
  };

  const handleReroute = async () => {
    if (!rerouteDialog || !rerouteTarget) { toast.error('Select a target stage'); return; }
    setOverrideLoading(true);
    try {
      await moveJobCardToStage(jobCardId, {
        batch_id: rerouteDialog.batchId || undefined,
        target_stage_key: rerouteTarget,
        reason: rerouteReason || undefined,
      });
      const target = (jobCard.production_stages || []).find(s => s.stage_key === rerouteTarget);
      toast.success(`Rerouted to ${target?.stage_name || 'stage'}`);
      setRerouteDialog(null);
      fetchJobCard();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to reroute');
    }
    setOverrideLoading(false);
  };

  const openInsertDialog = () => {
    setInsertStageKey('');
    setInsertAfter('__start__');
    setInsertDialog(true);
  };

  const handleInsert = async () => {
    if (!insertStageKey) { toast.error('Pick a process'); return; }
    setOverrideLoading(true);
    try {
      const res = await insertJobCardStage(jobCardId, {
        stage_key: insertStageKey,
        after_stage_key: insertAfter === '__start__' ? null : insertAfter,
      });
      // the supervisor is told what the goods on the floor will do with it,
      // because a part-run job splits over the answer
      const skipped = (res.already_past || []).reduce((n, b) => n + (b.quantity || 0), 0);
      toast.success(res.message + (skipped ? ` — ${skipped.toLocaleString()} pcs are already past it` : ''));
      setInsertDialog(false);
      fetchJobCard();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to add the process');
    }
    setOverrideLoading(false);
  };

  const openRemoveDialog = () => {
    setRemoveKey('');
    setRemoveDialog(true);
  };

  const handleRemove = async () => {
    if (!removeKey) { toast.error('Select a stage to remove'); return; }
    setOverrideLoading(true);
    try {
      await removeJobCardStage(jobCardId, { stage_key: removeKey });
      toast.success('Stage removed');
      setRemoveDialog(false);
      fetchJobCard();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to remove stage');
    }
    setOverrideLoading(false);
  };

  // ─── Add Remark ───
  const handleAddRemark = async () => {
    if (!remarkText.trim() || !jobCard?.order_uuid) return;
    setAddingRemark(true);
    try {
      await api.put(`/orders/${jobCard.order_uuid}/remarks`, { remark: remarkText.trim() });
      toast.success('Remark added');
      setRemarkText('');
      fetchJobCard();
    } catch (e) {
      toast.error('Failed to add remark');
    }
    setAddingRemark(false);
  };

  // ─── Send to Vendor ───
  const openVendorDialog = async () => {
    setVendorDialog(true);
    try {
      const res = await api.get('/vendors/list');
      setVendors(res.data.vendors || []);
    } catch (e) {
      setVendors([]);
    }
  };

  const handleSendToVendor = async () => {
    if (!selectedVendor) { toast.error('Select a vendor'); return; }
    setSendingVendor(true);
    try {
      await api.post('/vendors/send', {
        order_id: jobCard.order_uuid || jobCard.order_id,
        vendor_id: selectedVendor,
        line_item_id: jobCardId,
        assignment_type: vendorAssignType,
        special_instructions: vendorInstructions,
        expected_return_date: vendorExpectedDate || undefined,
      });
      toast.success('Sent to vendor');
      setVendorDialog(false);
      setSelectedVendor('');
      setVendorInstructions('');
      fetchJobCard();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send to vendor');
    }
    setSendingVendor(false);
  };

  // ─── Print / QR ───
  // two different sheets: the card for this one item, which is what goes to the
  // machine, and the order sheet covering every item on the order
  const handlePrintJobCard = async () => {
    if (!jobCardId) return;
    try {
      await openAuthedFile(`/jobcard/card/${jobCardId}/pdf`);
    } catch {
      toast.error('Could not open the job card');
    }
  };

  const handlePrintOrderSheet = async () => {
    if (!jobCard?.order_id) return;
    try {
      await openAuthedFile(`/jobcard/${jobCard.order_id}/pdf`);
    } catch {
      toast.error('Could not open the order sheet');
    }
  };

  const handleDownloadQR = () =>
    downloadJobCardQr(jobCardId, jobCard?.job_card_id || jobCard?.order_id);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand-blue"></div>
      </div>
    );
  }

  if (!jobCard) {
    return (
      <div className="max-w-4xl mx-auto py-12 text-center">
        <Package className="h-16 w-16 text-slate-300 mx-auto mb-4" />
        <h2 className="text-lg font-semibold text-slate-700">Job Card Not Found</h2>
        <Button variant="outline" className="mt-4" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-2" /> Go Back
        </Button>
      </div>
    );
  }

  const stages = jobCard.production_stages || [];
  const currentStageIndex = jobCard.current_stage_index || 0;
  const currentStage = stages[currentStageIndex];
  const progress = stages.length > 0 ? ((currentStageIndex / stages.length) * 100) : 0;
  const pColor = PRIORITY_COLORS[jobCard.priority] || PRIORITY_COLORS.medium;
  const sColor = STATUS_COLORS[jobCard.status] || STATUS_COLORS.in_progress;
  const batches = jobCard.batches || [];
  const specs = jobCard.specifications || {};
  const remarks = jobCard.remarks || [];

  // One command bar instead of eleven actions spread across three cards. The
  // stage overrides act on the batch that is standing at the stage, which is
  // what every one of them meant anyway.
  const primaryBatch = batches.find(b => b.status !== 'completed') || batches[0] || null;
  const batchIdOf = (b) => (b ? (b.batch_id || b.id) : undefined);
  const atFirst = (primaryBatch?.current_stage_index ?? currentStageIndex) <= 0;
  const atLast = (primaryBatch?.current_stage_index ?? currentStageIndex) >= stages.length - 1;
  const stageIdx = primaryBatch?.current_stage_index ?? currentStageIndex;
  const hereName = primaryBatch?.current_stage_name || currentStage?.stage_name;

  const batchColumns = [
    {
      key: 'batch',
      header: 'Batch',
      width: '8rem',
      render: (b, i) => <span className="font-semibold text-slate-900">#{b.batch_number || i + 1}</span>,
    },
    {
      key: 'current_stage_name',
      header: 'Standing at',
      width: '12rem',
      render: (b) => (
        <span className="block truncate text-[12px] font-medium text-brand-blue">{b.current_stage_name}</span>
      ),
    },
    {
      key: 'quantity',
      header: 'Qty',
      align: 'right',
      width: '7rem',
      render: (b) => <span className="font-medium">{b.quantity?.toLocaleString()}</span>,
      total: (rows) => rows.reduce((s, b) => s + (b.quantity || 0), 0).toLocaleString(),
    },
    {
      key: 'status',
      header: 'Status',
      width: '8rem',
      mobile: false,
      render: (b) => (
        <span className={`text-[12px] font-medium ${
          b.status === 'completed' ? 'text-emerald-600'
            : b.status === 'in_progress' ? 'text-blue-600' : 'text-slate-500'
        }`}>{b.status?.replace('_', ' ')}</span>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: '17rem',
      align: 'right',
      render: (b) => b.status === 'completed' ? null : (
        <span className="flex flex-wrap items-center justify-end gap-0.5">
          <CellButton icon={Cog} label="Machine" onClick={() => openMachineAssign(b)} />
          {(b.current_stage_index || 0) > 0 && (
            <CellButton
              icon={ArrowLeftIcon}
              label={stages[(b.current_stage_index || 0) - 1]?.stage_name || 'Back'}
              onClick={() => openMoveDialog('backward', batchIdOf(b), b.quantity,
                b.current_stage_name, stages[(b.current_stage_index || 0) - 1]?.stage_name)}
            />
          )}
          {(b.current_stage_index || 0) < stages.length - 1 && (
            <CellButton
              icon={ArrowRight}
              label={stages[(b.current_stage_index || 0) + 1]?.stage_name || 'Next'}
              tone="good"
              onClick={() => openMoveDialog('forward', batchIdOf(b), b.quantity,
                b.current_stage_name, stages[(b.current_stage_index || 0) + 1]?.stage_name)}
            />
          )}
          <CellButton icon={SkipForward} label="Skip" onClick={() => openSkipDialog(batchIdOf(b), b.current_stage_name)} />
          <CellButton icon={Shuffle} label="Reroute" onClick={() => openRerouteDialog(batchIdOf(b), b.current_stage_name)} />
        </span>
      ),
    },
  ];

  const singleBatchRow = batches.length === 0 ? [{
    id: jobCardId,
    batch_id: jobCardId,
    batch_number: 1,
    current_stage_name: currentStage?.stage_name,
    current_stage_index: currentStageIndex,
    quantity: jobCard.quantity,
    status: jobCard.status === 'completed' ? 'completed' : 'in_progress',
  }] : batches;

  return (
    <Screen data-testid="supervisor-job-card-detail-page">
      <CommandBar
        title={
          // the firm is who the job is for; the person named on it is who to
          // ring about it. The order number it used to lead with is a fact of
          // the job, and reads as one in the band below.
          <span className="flex min-w-0 flex-col leading-tight">
            <span className="truncate">{jobCard.firm_name || jobCard.customer_name}</span>
            {jobCard.firm_name && jobCard.customer_name && (
              <span className="truncate text-[11px] font-normal text-slate-500">
                {jobCard.customer_name}
              </span>
            )}
          </span>
        }
        onBack={() => navigate(-1)}
        badges={
          <>
            <Badge className={`text-[10px] ${pColor.class}`}>{jobCard.priority}</Badge>
            <Badge className={`text-[10px] ${sColor.class}`}>{(jobCard.status || '').replace('_', ' ')}</Badge>
            <span className="text-[11px] text-slate-500">
              {currentStage?.stage_name || 'Completed'} · {currentStageIndex + 1}/{stages.length}
            </span>
          </>
        }
        actions={[
          canMoveStages && jobCard.status !== 'completed' && {
            key: 'machine', label: 'Machine', icon: Cog,
            onClick: () => openMachineAssign(primaryBatch || {
              id: jobCardId, batch_id: jobCardId,
              current_stage_name: hereName, current_stage_index: stageIdx, quantity: jobCard.quantity,
            }),
          },
          canMoveStages && jobCard.status !== 'completed' && !atFirst && {
            key: 'back', label: stages[stageIdx - 1]?.stage_name || 'Back', icon: ArrowLeftIcon,
            onClick: () => openMoveDialog('backward', batchIdOf(primaryBatch),
              primaryBatch?.quantity ?? jobCard.quantity, hereName, stages[stageIdx - 1]?.stage_name),
          },
          canMoveStages && jobCard.status !== 'completed' && !atLast && {
            key: 'next', label: stages[stageIdx + 1]?.stage_name || 'Next', icon: ArrowRight, tone: 'good',
            onClick: () => openMoveDialog('forward', batchIdOf(primaryBatch),
              primaryBatch?.quantity ?? jobCard.quantity, hereName, stages[stageIdx + 1]?.stage_name),
          },
          canMoveStages && jobCard.status !== 'completed' && {
            key: 'insert', label: 'Add process', icon: Plus, onClick: openInsertDialog,
          },
          canMoveStages && jobCard.status !== 'completed' && {
            key: 'remove', label: 'Remove stage', icon: Trash2, tone: 'danger', onClick: openRemoveDialog,
          },
          { key: 'vendor', label: 'Send to Vendor', icon: Truck, onClick: openVendorDialog },
          { key: 'print', label: 'Print Job Card', icon: Printer, onClick: handlePrintJobCard },
          { key: 'print-order', label: 'Print Order Sheet', icon: FileText, onClick: handlePrintOrderSheet },
          { key: 'qr', label: 'QR Sticker', icon: QrCode, onClick: handleDownloadQR },
          { key: 'refresh', label: 'Refresh', icon: RefreshCw, onClick: fetchJobCard },
        ]}
      />

      <FactBand>
        <Fact label="Order">{jobCard.order_id}</Fact>
        <Fact label="Product">{jobCard.product_type || jobCard.order_type}</Fact>
        <Fact label="Firm">{jobCard.firm_name}</Fact>
        <Fact label="Voice #">{jobCard.voice_number}</Fact>
        <Fact label="Customer">{jobCard.customer_name}</Fact>
        <Fact label="Quantity">{jobCard.quantity ? `${jobCard.quantity.toLocaleString()} pcs` : '—'}</Fact>
        <Fact label="Delivery">
          {jobCard.expected_delivery_date ? formatIST(jobCard.expected_delivery_date, { dateOnly: true }) : '—'}
        </Fact>
        <Fact label="Progress">{Math.round(progress)}% · stage {currentStageIndex + 1} of {stages.length}</Fact>
        {needsPlate(jobCard) && (
          <Fact label="Plate" valueClassName={
            jobCard.plate_status === 'received' ? 'text-emerald-600'
              : jobCard.plate_status === 'ordered' ? 'text-amber-600' : 'text-red-600'
          }>
            {(jobCard.plate_status || 'pending').toUpperCase()}
            {jobCard.plate_ordered_vendor ? ` · ${jobCard.plate_ordered_vendor}` : ''}
          </Fact>
        )}
      </FactBand>

      <ScreenSplit
        railWidth="22rem"
        rail={
          <>
            <Pane title="Pipeline" aside={`${stages.length} stages`} className="shrink-0">
              <div className="flex flex-wrap gap-1 p-2">
                {stages.map((stage, idx) => {
                  const done = idx < currentStageIndex;
                  const here = idx === currentStageIndex;
                  // any batch sent back marks the stage — reading only the first
                  // hid every revisit on a split job's later runs
                  const revisited = batches.some(
                    b => b.revisited_stages?.includes(stage.stage_name)
                  );
                  return (
                    <span
                      key={idx}
                      title={stage.completed_at ? formatIST(stage.completed_at, { compact: true }) : undefined}
                      className={`rounded-full border px-1.5 py-0.5 text-[10px] ${
                        done ? 'border-green-200 bg-green-50 text-green-700'
                          : here ? 'border-brand-orange/40 bg-orange-50 font-semibold text-brand-orange'
                            : 'border-slate-200 bg-slate-50 text-slate-400'
                      }`}
                    >
                      {stage.stage_name}
                      {revisited && <span className="ml-0.5 text-amber-600">↻</span>}
                    </span>
                  );
                })}
              </div>
            </Pane>

            <Pane title="Specifications" aside={`${Object.keys(specs).length} fields`} collapsible className="min-h-0">
              <div className="p-1.5">
                <FactBand min="100%" className="border-0 p-0 shadow-none">
                  {Object.entries(specs).map(([key, value]) => (
                    <Fact key={key} label={key.replace(/_/g, ' ')}>
                      {Array.isArray(value) ? value.join(', ') : String(value ?? '')}
                    </Fact>
                  ))}
                </FactBand>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2 w-full"
                  onClick={() => navigate(`/supervisor/job-card/${jobCardId}/edit`)}
                >
                  <Edit className="mr-1 h-3.5 w-3.5" /> Edit specifications
                </Button>
              </div>
            </Pane>

            <Pane title="Remarks" aside={`${remarks.length}`} className="min-h-0 flex-1">
              <div className="space-y-1.5 p-2">
                {Array.isArray(remarks) && remarks.map((r, idx) => (
                  <p key={idx} className="rounded bg-slate-50 px-2 py-1 text-[11px] text-slate-600">
                    {typeof r === 'string' ? r : r.text || JSON.stringify(r)}
                    {r.by && <span className="ml-1 text-slate-400">— {r.by}</span>}
                  </p>
                ))}
                {remarks.length === 0 && (
                  <p className="py-2 text-center text-[11px] text-slate-400">Nothing noted yet</p>
                )}
                <div className="flex gap-1.5 pt-1">
                  <Textarea
                    placeholder="Add a remark…"
                    value={remarkText}
                    onChange={(e) => setRemarkText(e.target.value)}
                    rows={2}
                    className="resize-none text-xs"
                  />
                  <Button size="sm" className="h-auto shrink-0"
                    disabled={!remarkText.trim() || addingRemark} onClick={handleAddRemark}>
                    <MessageSquare className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            </Pane>
          </>
        }
      >
        <Pane
          title="Batches"
          aside={batches.length ? `${batches.length}` : 'single run'}
          scroll={false}
          bodyClassName="flex flex-col"
        >
          <DataGrid
            data-testid="supervisor-batches-table"
            columns={batchColumns}
            rows={singleBatchRow}
            getRowId={(b, i) => b.id || b.batch_id || i}
            showTotals={singleBatchRow.length > 1}
            empty="No batches on this card yet"
          />
        </Pane>
      </ScreenSplit>

      {/* ═══════════ DIALOGS ═══════════ */}

      {/* Move Forward/Backward Dialog */}
      <Dialog open={!!moveDialog} onOpenChange={() => setMoveDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{moveDialog?.direction === 'backward' ? 'Move Backward' : 'Move Forward'}</DialogTitle>
            <DialogDescription>
              {moveDialog?.direction === 'backward' ? 'Move from' : 'Move from'}{' '}
              <strong>{moveDialog?.fromStage}</strong> {moveDialog?.direction === 'backward' ? 'back to' : 'to'}{' '}
              <strong>{moveDialog?.toStage}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Full/Partial toggle */}
            <div className="flex gap-2">
              <Button
                variant={moveQtyMode === 'full' ? 'default' : 'outline'}
                size="sm"
                className="flex-1"
                onClick={() => { setMoveQtyMode('full'); setMoveQty(String(moveDialog?.batchQty || 0)); }}
              >
                Full ({moveDialog?.batchQty})
              </Button>
              <Button
                variant={moveQtyMode === 'partial' ? 'default' : 'outline'}
                size="sm"
                className="flex-1"
                onClick={() => { setMoveQtyMode('partial'); setMoveQty(''); }}
              >
                Partial (Split)
              </Button>
            </div>
            {moveQtyMode === 'partial' && (
              <div>
                <label className="text-sm text-slate-600">Quantity to move (of {moveDialog?.batchQty} total):</label>
                <Input
                  type="number"
                  value={moveQty}
                  onChange={(e) => setMoveQty(e.target.value)}
                  placeholder="Enter quantity"
                  min={1}
                  max={moveDialog?.batchQty}
                  className="mt-1"
                />
              </div>
            )}
            {moveDialog?.direction === 'backward' && (
              <>
                <div className="text-xs text-amber-800 bg-amber-50 p-2 rounded-lg">
                  This is a correction/override. The stage will be marked as "revisited".
                </div>
                <Textarea
                  value={moveReason}
                  onChange={(e) => setMoveReason(e.target.value)}
                  placeholder="Reason for moving back (optional)"
                  rows={2}
                />
              </>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setMoveDialog(null)}>Cancel</Button>
            <Button
              onClick={handleMoveConfirm}
              disabled={moveLoading}
              className={moveDialog?.direction === 'forward' ? 'bg-green-600 hover:bg-green-700' : 'bg-amber-600 hover:bg-amber-700'}
            >
              {moveLoading ? 'Moving...' : moveDialog?.direction === 'backward' ? 'Move Back' : 'Move Forward'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Machine Assignment Dialog */}
      <Dialog open={!!machineDialog} onOpenChange={() => setMachineDialog(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Select Machine</DialogTitle>
            <DialogDescription>Stage: {machineDialog?.stageName}</DialogDescription>
          </DialogHeader>
          {loadingMachines ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-blue"></div>
            </div>
          ) : machines.length === 0 ? (
            <div className="py-8 text-center">
              <Cog className="h-10 w-10 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-500">No machines linked to this stage</p>
            </div>
          ) : (
            <div className="space-y-1 max-h-64 overflow-y-auto">
              {machines.map((m) => (
                <button
                  key={m.id}
                  onClick={() => assignMachine(machineDialog.batch, m)}
                  disabled={assigning}
                  className="w-full flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-slate-50 transition-colors text-left border border-transparent hover:border-slate-200"
                >
                  <div className="w-9 h-9 rounded-full bg-brand-blue flex items-center justify-center flex-shrink-0">
                    <Cog className="h-4 w-4 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-800">{m.machine_name}</p>
                    {m.machine_type && <p className="text-[11px] text-slate-500">{m.machine_type}</p>}
                  </div>
                  {assigning ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-brand-blue"></div>
                  ) : (
                    <ChevronRight className="h-4 w-4 text-brand-blue" />
                  )}
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Send to Vendor Dialog */}
      <Dialog open={vendorDialog} onOpenChange={setVendorDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Send to Vendor</DialogTitle>
            <DialogDescription>Send this job card for external processing</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-slate-700">Vendor</label>
              <Select value={selectedVendor} onValueChange={setSelectedVendor}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Select vendor" /></SelectTrigger>
                <SelectContent>
                  {vendors.map(v => (
                    <SelectItem key={v.id} value={v.id}>{v.full_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Assignment Type</label>
              <Select value={vendorAssignType} onValueChange={setVendorAssignType}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ASSIGNMENT_TYPES.map(t => (
                    <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Instructions (optional)</label>
              <Textarea
                value={vendorInstructions}
                onChange={(e) => setVendorInstructions(e.target.value)}
                placeholder="Special instructions..."
                rows={2}
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Expected Return Date (optional)</label>
              <Input
                type="date"
                value={vendorExpectedDate}
                onChange={(e) => setVendorExpectedDate(e.target.value)}
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setVendorDialog(false)}>Cancel</Button>
            <Button onClick={handleSendToVendor} disabled={sendingVendor} className="bg-violet-600 hover:bg-violet-700">
              {sendingVendor ? 'Sending...' : 'Send to Vendor'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Skip Stage Dialog */}
      <Dialog open={!!skipDialog} onOpenChange={() => setSkipDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Skip Stage</DialogTitle>
            <DialogDescription>
              Skip <strong>{skipDialog?.stageName}</strong> and advance to the next stage. This is a supervisor override.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-sm text-slate-600">Reason (optional)</label>
            <Textarea
              value={skipReason}
              onChange={(e) => setSkipReason(e.target.value)}
              placeholder="Why is this stage being skipped?"
              rows={2}
            />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setSkipDialog(null)}>Cancel</Button>
            <Button onClick={handleSkip} disabled={overrideLoading} className="bg-slate-700 hover:bg-slate-800">
              {overrideLoading ? 'Skipping...' : 'Skip Stage'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reroute Dialog */}
      <Dialog open={!!rerouteDialog} onOpenChange={() => setRerouteDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reroute to Stage</DialogTitle>
            <DialogDescription>
              Jump from <strong>{rerouteDialog?.stageName}</strong> to any stage in the pipeline.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-slate-700">Target Stage</label>
              <Select value={rerouteTarget} onValueChange={setRerouteTarget}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Select stage" /></SelectTrigger>
                <SelectContent>
                  {(jobCard.production_stages || []).map((s, idx) => (
                    <SelectItem key={s.stage_key || idx} value={s.stage_key}>
                      {idx + 1}. {s.stage_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm text-slate-600">Reason (optional)</label>
              <Textarea
                value={rerouteReason}
                onChange={(e) => setRerouteReason(e.target.value)}
                placeholder="Why is this being rerouted?"
                rows={2}
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setRerouteDialog(null)}>Cancel</Button>
            <Button onClick={handleReroute} disabled={overrideLoading} className="bg-indigo-600 hover:bg-indigo-700">
              {overrideLoading ? 'Rerouting...' : 'Reroute'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Insert Stage Dialog */}
      <Dialog open={insertDialog} onOpenChange={setInsertDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add a process</DialogTitle>
            <DialogDescription>One more process, put into a job that is already running.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-slate-700">Process</label>
              <Select value={insertStageKey} onValueChange={setInsertStageKey}>
                <SelectTrigger className="mt-1" data-testid="insert-stage-select">
                  <SelectValue placeholder="Pick a process" />
                </SelectTrigger>
                <SelectContent>
                  {addableStages.map(rule => (
                    <SelectItem key={rule.stage_key} value={rule.stage_key}>{rule.stage_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Position</label>
              <Select value={insertAfter} onValueChange={setInsertAfter}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__start__">At start</SelectItem>
                  {(jobCard.production_stages || []).map((s, idx) => (
                    <SelectItem key={s.stage_key || idx} value={s.stage_key}>
                      After {idx + 1}. {s.stage_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <p className="text-xs text-slate-500">
              Goods already past that point of the line keep going — they skip it.
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setInsertDialog(false)}>Cancel</Button>
            <Button onClick={handleInsert} disabled={overrideLoading || !insertStageKey}
              className="bg-brand-blue hover:bg-brand-blue-dark">
              {overrideLoading ? 'Adding...' : 'Add Process'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove Stage Dialog */}
      <Dialog open={removeDialog} onOpenChange={setRemoveDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Remove Stage</DialogTitle>
            <DialogDescription>
              Remove a stage from the pipeline. Completed or in-progress stages cannot be removed.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">Stage</label>
            <Select value={removeKey} onValueChange={setRemoveKey}>
              <SelectTrigger className="mt-1"><SelectValue placeholder="Select stage" /></SelectTrigger>
              <SelectContent>
                {(jobCard.production_stages || []).map((s, idx) => (
                  <SelectItem key={s.stage_key || idx} value={s.stage_key}>
                    {idx + 1}. {s.stage_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setRemoveDialog(false)}>Cancel</Button>
            <Button onClick={handleRemove} disabled={overrideLoading} className="bg-red-600 hover:bg-red-700">
              {overrideLoading ? 'Removing...' : 'Remove Stage'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Screen>
  );
}

